/**
 * Client-side feature gate storage.
 *
 * Feature gates are loaded from the server after login (on the user object)
 * and cached in localStorage so subsequent page loads can read them
 * synchronously without waiting for a round trip.
 *
 * The same storage key is used across apps — student, teacher, and admin
 * run on different subdomains / ports, so their localStorage is already
 * isolated by the browser's same-origin policy.
 */
import type { FeatureGateName } from '@padcanvas/shared';

const STORAGE_KEY = 'feature_gates';

export function setStoredFeatureGates(gates: Record<string, boolean>): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(gates));
}

export function clearStoredFeatureGates(): void {
  localStorage.removeItem(STORAGE_KEY);
}

export function getStoredFeatureGates(): Record<string, boolean> {
  const str = localStorage.getItem(STORAGE_KEY);
  if (!str) return {};
  try {
    return JSON.parse(str) as Record<string, boolean>;
  } catch {
    return {};
  }
}

/**
 * Read a feature gate. `gateName` is constrained to `FeatureGateName` (the
 * canonical list in @padcanvas/shared) — referencing a gate not listed there is
 * a compile error, so the frontend can never use an unenumerated gate.
 */
export function isFeatureEnabled(gateName: FeatureGateName): boolean {
  return !!getStoredFeatureGates()[gateName];
}

/**
 * Experiment variant assignments ({ experimentKey: variantName }).
 *
 * Resolved deterministically server-side at login and cached here so the UI can
 * branch on a variant synchronously. Stored next to feature gates; same
 * per-origin isolation applies.
 * See docs/FeatureDesigns/first-run/lifecycle-stage1-2-first-run-habit.md §Experimentation.
 */
const EXPERIMENTS_KEY = 'experiments';

export function setStoredExperiments(experiments: Record<string, string>): void {
  localStorage.setItem(EXPERIMENTS_KEY, JSON.stringify(experiments));
}

export function clearStoredExperiments(): void {
  localStorage.removeItem(EXPERIMENTS_KEY);
}

export function getStoredExperiments(): Record<string, string> {
  const str = localStorage.getItem(EXPERIMENTS_KEY);
  if (!str) return {};
  try {
    return JSON.parse(str) as Record<string, string>;
  } catch {
    return {};
  }
}

/** The assigned variant for an experiment, or null if the user isn't bucketed. */
export function getExperimentVariant(experimentKey: string): string | null {
  return getStoredExperiments()[experimentKey] ?? null;
}
